import React, { useState, useMemo, useCallback } from 'react';
import {
  MdSchedule, MdCheckCircle, MdBiotech, MdScience, MdVerified, MdAdd, MdDelete, MdArrowBack,
} from 'react-icons/md';
import { PageShell, Badge, DataTable, StatCard } from '../../components/Ui';
import { useAuth } from '../../auth/AuthContext';
import { ipApi } from '../../services/api';
import { useLiveData } from '../../hooks/useLiveData';
import { useHospitalProfile } from '../../hooks/useHospitalProfile';
import { printLabReport } from '../../utils/hospitalPdf';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const STATUS_LABEL = { ordered: 'Ordered', collected: 'Sample collected', resulted: 'Results entered', verified: 'Verified', cancelled: 'Cancelled' };
function statusTone(s) {
  if (s === 'verified' || s === 'Approved' || s === 'completed') return 'success';
  if (s === 'resulted') return 'info';
  if (s === 'collected') return 'warning';
  if (s === 'cancelled') return 'danger';
  return 'default';
}

function flagFor(value, refLow, refHigh) {
  const v = Number(value);
  if (value === '' || value == null || Number.isNaN(v)) return '';
  const lo = refLow === '' || refLow == null ? null : Number(refLow);
  const hi = refHigh === '' || refHigh == null ? null : Number(refHigh);
  if (hi != null && !Number.isNaN(hi) && v > hi) return 'H';
  if (lo != null && !Number.isNaN(lo) && v < lo) return 'L';
  if (lo != null || hi != null) return 'N';
  return '';
}
const flagColor = (f) => (f === 'H' ? '#dc2626' : f === 'L' ? '#2563eb' : '#64748b');

// Turn a catalog test into an order-test with blank result values
function catalogToOrderTest(t) {
  const params = Array.isArray(t.parameters) && t.parameters.length
    ? t.parameters
    : [{ name: t.name, unit: t.unit || '', refRange: t.refRange || '', refLow: t.refLow ?? '', refHigh: t.refHigh ?? '' }];
  return {
    testId: t.testId, name: t.name, rate: Number(t.rate || 0), sampleType: t.sampleType || 'Blood',
    parameters: params.map((p) => ({ ...p, value: '' })),
  };
}

const EMPTY_TEST = { name: '', category: 'General', sampleType: 'Blood', rate: '', unit: '', refRange: '', refLow: '', refHigh: '', method: '', parameters: [] };

export default function LabOrders() {
  const { user } = useAuth();
  const { hospital } = useHospitalProfile();
  const { data, loading, error, reload } = useLiveData(() => ipApi.getLabOrders(80), []);
  const { data: catData, loading: catLoading, reload: reloadCat } = useLiveData(() => ipApi.getLabCatalog(), []);
  const orders = useMemo(() => data?.orders || [], [data]);
  const catalog = useMemo(() => catData?.items || [], [catData]);

  const [tab, setTab] = useState('orders');
  const [resultOrder, setResultOrder] = useState(null);
  const [msg, setMsg] = useState('');

  const pending = orders.filter((r) => ['ordered', 'collected'].includes(r.status));
  const toVerify = orders.filter((r) => r.status === 'resulted');
  const verified = orders.filter((r) => ['verified', 'Approved', 'completed'].includes(r.status));
  const revenue = orders.reduce((s, r) => s + Number(r.amount || 0), 0);

  return (
    <PageShell
      title="Laboratory"
      subtitle="Test catalog · orders · result entry & verification"
      actions={<button type="button" className="btn btn-secondary btn-sm" onClick={() => { reload(); reloadCat(); }}>Refresh</button>}
    >
      <div className="stat-grid">
        <StatCard label="Pending / collection" value={pending.length} accent="#f59e0b" icon={MdSchedule} loading={loading} />
        <StatCard label="Awaiting verify" value={toVerify.length} accent="#06b6d4" icon={MdScience} loading={loading} />
        <StatCard label="Verified" value={verified.length} accent="#10b981" icon={MdVerified} loading={loading} />
        <StatCard label="Tests in catalog" value={catalog.length} accent="#8b5cf6" icon={MdBiotech} loading={catLoading} />
        <StatCard label="Revenue" value={inr(revenue)} accent="#0ea5e9" icon={MdCheckCircle} loading={loading} />
      </div>

      {error && <div className="error mb">{error}</div>}
      {msg && <div className={`mb ${/fail|error|not|exceed/i.test(msg) ? 'error' : 'info-banner'}`}>{msg}</div>}

      {resultOrder ? (
        <ResultEntry
          order={resultOrder}
          onBack={() => setResultOrder(null)}
          onSaved={async (m) => { setResultOrder(null); setMsg(m); await reload(); }}
          onError={setMsg}
        />
      ) : (
        <>
          <div className="period-tabs card mb">
            <button type="button" className={`emr-chip ${tab === 'orders' ? 'active' : ''}`} onClick={() => setTab('orders')}>Orders ({pending.length + toVerify.length})</button>
            <button type="button" className={`emr-chip ${tab === 'new' ? 'active' : ''}`} onClick={() => setTab('new')}>+ New order</button>
            <button type="button" className={`emr-chip ${tab === 'catalog' ? 'active' : ''}`} onClick={() => setTab('catalog')}>Test catalog ({catalog.length})</button>
            <button type="button" className={`emr-chip ${tab === 'reports' ? 'active' : ''}`} onClick={() => setTab('reports')}>📊 Reports</button>
            <button type="button" className={`emr-chip ${tab === 'critical' ? 'active' : ''}`} onClick={() => setTab('critical')}>🚨 Critical Values</button>
            <button type="button" className={`emr-chip ${tab === 'samples' ? 'active' : ''}`} onClick={() => setTab('samples')}>🧪 Sample Collection</button>
          </div>

          {tab === 'orders' && (
            <OrdersTab
              orders={orders} loading={loading}
              onCollect={async (o) => { await ipApi.patchLabOrder(o.orderId, { status: 'collected' }); await reload(); }}
              onEnter={(o) => setResultOrder(o)}
              onVerify={async (o) => { await ipApi.patchLabOrder(o.orderId, { status: 'verified' }); setMsg('Report verified.'); await reload(); }}
              onPrint={(o) => printLabReport(o, hospital, user)}
            />
          )}
          {tab === 'new' && (
            <NewOrder
              catalog={catalog} user={user}
              onDone={async (m) => { setMsg(m); setTab('orders'); await reload(); }}
              onError={setMsg}
            />
          )}
          {tab === 'catalog' && (
            <CatalogTab
              catalog={catalog} loading={catLoading}
              onChanged={async (m) => { if (m) setMsg(m); await reloadCat(); }}
              onError={setMsg}
            />
          )}
          {tab === 'reports' && <LabReportsTab orders={orders} catalog={catalog} />}
          {tab === 'critical' && <CriticalValuesTab orders={orders} />}
          {tab === 'samples' && (
            <SampleCollectionTab
              orders={orders}
              onCollect={async (o) => { await ipApi.patchLabOrder(o.orderId, { status: 'collected', collectedAt: new Date().toISOString() }); await reload(); }}
            />
          )}
        </>
      )}
    </PageShell>
  );
}

/* ── Lab Reports Tab ── */
function LabReportsTab({ orders, catalog }) {
  const total = orders.length;
  const verified = orders.filter((o) => ['verified', 'Approved', 'completed'].includes(o.status)).length;
  const revenue = orders.reduce((s, o) => s + Number(o.amount || 0), 0);
  const tatSamples = orders
    .filter((o) => o.createdAt && o.verifiedAt)
    .map((o) => (new Date(o.verifiedAt) - new Date(o.createdAt)) / 60000)
    .filter((m) => m > 0 && m < 7 * 24 * 60);
  const avgTAT = tatSamples.length > 0 ? Math.round(tatSamples.reduce((a, b) => a + b, 0) / tatSamples.length) : 0;

  const byStatus = [
    { label: 'Ordered', count: orders.filter((o) => o.status === 'ordered').length, color: '#94a3b8' },
    { label: 'Sample Collected', count: orders.filter((o) => o.status === 'collected').length, color: '#f59e0b' },
    { label: 'Results Entered', count: orders.filter((o) => o.status === 'resulted').length, color: '#3b82f6' },
    { label: 'Verified', count: orders.filter((o) => ['verified', 'Approved', 'completed'].includes(o.status)).length, color: '#10b981' },
    { label: 'Cancelled', count: orders.filter((o) => o.status === 'cancelled').length, color: '#ef4444' },
  ];

  const topTests = [...catalog].sort((a, b) => Number(b.rate || 0) - Number(a.rate || 0)).slice(0, 6);

  return (
    <div>
      <div className="stat-grid" style={{ marginBottom: 14 }}>
        {[
          { label: 'Total Orders', value: total, color: '#6366f1' },
          { label: 'Verified Reports', value: verified, color: '#10b981' },
          { label: 'Total Revenue', value: `₹${revenue.toLocaleString('en-IN')}`, color: '#0d6e6a' },
          { label: 'Avg TAT (mins)', value: `${avgTAT} min`, color: '#f59e0b' },
        ].map(({ label, value, color }) => (
          <div key={label} className="card" style={{ textAlign: 'center', padding: 16 }}>
            <div style={{ fontFamily: 'Outfit, sans-serif', fontSize: 22, fontWeight: 900, color }}>{value}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{label}</div>
          </div>
        ))}
      </div>

      <div className="closing-report-grid">
        <div className="closing-section">
          <div className="closing-section-title">📊 Order Status Breakdown</div>
          {byStatus.map(({ label, count, color }) => (
            <div key={label} className="closing-row">
              <span className="closing-row-label">{label}</span>
              <div style={{ flex: 1, height: 8, background: '#f1f5f9', borderRadius: 999, margin: '0 8px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: total > 0 ? `${(count / total) * 100}%` : '0%', background: color, borderRadius: 999, transition: 'width 0.5s' }} />
              </div>
              <span style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 800, fontSize: 14, color, minWidth: 28 }}>{count}</span>
            </div>
          ))}
        </div>
        <div className="closing-section">
          <div className="closing-section-title">🧪 Top Tests by Rate</div>
          {topTests.map((t, i) => (
            <div key={t.testId || i} className="closing-row">
              <span className="closing-row-label">{t.name}</span>
              <span className="closing-row-count">{t.sampleType || 'Blood'}</span>
              <span className="closing-row-total" style={{ color: '#0d6e6a' }}>₹{Number(t.rate || 0).toLocaleString('en-IN')}</span>
            </div>
          ))}
          {topTests.length === 0 && <p className="hint">No catalog data.</p>}
        </div>
      </div>
    </div>
  );
}

/* ── Orders list ── */
function OrdersTab({ orders, loading, onCollect, onEnter, onVerify, onPrint }) {
  const [filter, setFilter] = useState('active');
  const [busy, setBusy] = useState('');
  const active = orders.filter((r) => ['ordered', 'collected', 'resulted'].includes(r.status));
  const done = orders.filter((r) => ['verified', 'Approved', 'completed'].includes(r.status));
  const rows = filter === 'active' ? active : filter === 'done' ? done : orders;

  const run = async (fn, id) => { setBusy(id); try { await fn(); } finally { setBusy(''); } };

  const columns = [
    { key: 'orderId', label: 'Sample ID' },
    {
      key: 'patientName', label: 'Patient',
      render: (r) => <div><strong>{r.patientName || '—'}</strong>{(r.age || r.gender) && <div className="hint">{[r.gender, r.age && `${r.age}y`].filter(Boolean).join(' · ')}</div>}</div>,
    },
    { key: 'test', label: 'Tests', render: (r) => <span title={r.test}>{(r.test || (r.tests || []).map((t) => t.name).join(', ') || '—').slice(0, 50)}</span> },
    { key: 'amount', label: 'Amount', render: (r) => inr(r.amount) },
    { key: 'status', label: 'Status', render: (r) => <Badge tone={statusTone(r.status)}>{STATUS_LABEL[r.status] || r.status}</Badge> },
    {
      key: 'actions', label: 'Actions',
      render: (r) => (
        <div className="row-actions">
          {r.status === 'ordered' && <button type="button" className="btn btn-sm btn-secondary" disabled={busy === r.orderId} onClick={() => run(() => onCollect(r), r.orderId)}>Collect</button>}
          {['ordered', 'collected', 'resulted'].includes(r.status) && <button type="button" className="btn btn-sm btn-primary" onClick={() => onEnter(r)}>{r.status === 'resulted' ? 'Edit results' : 'Enter results'}</button>}
          {r.status === 'resulted' && <button type="button" className="btn btn-sm btn-secondary" disabled={busy === r.orderId} onClick={() => run(() => onVerify(r), r.orderId)}>Verify</button>}
          {['verified', 'Approved', 'completed'].includes(r.status) && <button type="button" className="btn btn-sm btn-secondary" onClick={() => onPrint(r)}>Report PDF</button>}
        </div>
      ),
    },
  ];

  return (
    <>
      <div className="staff-filter-tabs mb">
        <button type="button" className={`tab-chip ${filter === 'active' ? 'active' : ''}`} onClick={() => setFilter('active')}>Active ({active.length})</button>
        <button type="button" className={`tab-chip ${filter === 'done' ? 'active' : ''}`} onClick={() => setFilter('done')}>Verified ({done.length})</button>
        <button type="button" className={`tab-chip ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>All ({orders.length})</button>
      </div>
      <DataTable columns={columns} rows={rows} keyField="orderId" loading={loading} emptyMessage="No lab orders here. Investigations from EMR and walk-ins appear in this list." />
    </>
  );
}

/* ── Result entry & verification ── */
function ResultEntry({ order, onBack, onSaved, onError }) {
  const [tests, setTests] = useState(() => {
    if (Array.isArray(order.tests) && order.tests.length) return JSON.parse(JSON.stringify(order.tests));
    return [{ name: order.test || 'Result', rate: order.amount || 0, parameters: [{ name: order.test || 'Result', unit: '', refRange: '', refLow: '', refHigh: '', value: order.result || '' }] }];
  });
  const [busy, setBusy] = useState(false);

  const setValue = (ti, pi, value) => setTests((ts) => ts.map((t, i) => i !== ti ? t : {
    ...t, parameters: t.parameters.map((p, j) => (j === pi ? { ...p, value } : p)),
  }));

  const save = async (verify) => {
    setBusy(true);
    try {
      await ipApi.patchLabOrder(order.orderId, { tests, status: verify ? 'verified' : 'resulted' });
      onSaved(verify ? 'Results saved and report verified.' : 'Results saved.');
    } catch (e) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <div className="row-actions mb" style={{ justifyContent: 'space-between' }}>
        <button type="button" className="btn btn-secondary btn-sm" onClick={onBack}><MdArrowBack /> Back</button>
        <div className="hint">{order.orderId} · {order.patientName}</div>
      </div>
      <h3>Enter results</h3>
      {tests.map((t, ti) => (
        <div key={ti} className="lab-result-block">
          <div className="lab-result-title">{t.name}</div>
          <div className="table-wrap">
            <table className="data-table">
              <thead><tr><th>Parameter</th><th>Result</th><th>Unit</th><th>Reference</th><th>Flag</th></tr></thead>
              <tbody>
                {t.parameters.map((p, pi) => {
                  const f = flagFor(p.value, p.refLow, p.refHigh);
                  return (
                    <tr key={pi}>
                      <td>{p.name}</td>
                      <td><input className="input input-sm" style={{ width: 120 }} value={p.value ?? ''} onChange={(e) => setValue(ti, pi, e.target.value)} /></td>
                      <td>{p.unit || '—'}</td>
                      <td>{p.refRange || (p.refLow !== '' || p.refHigh !== '' ? `${p.refLow ?? ''}–${p.refHigh ?? ''}` : '—')}</td>
                      <td style={{ color: flagColor(f), fontWeight: 700 }}>{f || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      <div className="form-actions">
        <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => save(false)}>Save results</button>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={() => save(true)}>Save & verify</button>
      </div>
    </div>
  );
}

/* ── New order ── */

const TEST_PANELS = [
  {
    id: 'cbc', name: 'CBC', label: 'Complete Blood Count (CBC)', sampleType: 'Blood', rate: 490, color: '#2563eb',
    parameters: [
      { name: 'Total WBC Count', unit: '/Cumm', refLow: 4000, refHigh: 10000, refRange: '4000–10000/Cumm' },
      { name: 'Polymorphs', unit: '%', refLow: 30, refHigh: 65, refRange: 'P: 30–65%' },
      { name: 'Lymphocytes', unit: '%', refLow: 20, refHigh: 35, refRange: 'L: 20–35%' },
      { name: 'Eosinophil', unit: '%', refLow: 2, refHigh: 6, refRange: 'E: 02–06%' },
      { name: 'Monocytes', unit: '%', refLow: 2, refHigh: 4, refRange: 'M: 02–04%' },
      { name: 'Haemoglobin', unit: 'Grams%', refLow: 11.4, refHigh: 16.2, refRange: 'M:13.4–18.0 / F:11.4–16.2 Grams%' },
      { name: 'Platelet Count', unit: 'Laks/Cumm', refLow: 1.5, refHigh: 4, refRange: '1.5–4 Laks/Cumm' },
      { name: 'RBC Count', unit: 'Millions/Cumm', refLow: 3.5, refHigh: 5.5, refRange: '3.5–5.5 millions/Cumm' },
      { name: 'P.C.V', unit: '%', refLow: 40, refHigh: 54, refRange: '40–54%' },
      { name: 'M.C.V', unit: 'c.microns', refLow: 78, refHigh: 94, refRange: '78.0–94.0 c.microns' },
      { name: 'M.C.H', unit: 'PG', refLow: 27, refHigh: 32, refRange: '27–32 PG' },
      { name: 'M.C.H.C', unit: '%', refLow: 30, refHigh: 36, refRange: '30–36%' },
    ],
  },
  {
    id: 'bsu', name: 'Blood Sugar+Urea+Creatinine', label: 'Blood Sugar / Urea / Creatinine', sampleType: 'Blood', rate: 490, color: '#dc2626',
    parameters: [
      { name: 'Random Blood Sugar', unit: 'mg/dl', refLow: 70, refHigh: 140, refRange: '70–140 mg/dl' },
      { name: 'Blood Urea', unit: 'mg/dl', refLow: 15, refHigh: 45, refRange: '15–45 mg/dl' },
      { name: 'Serum Creatinine', unit: 'mg/dl', refLow: 0.6, refHigh: 1.4, refRange: 'M:0.6–1.4 / F:0.6–1.2 mg/dl' },
    ],
  },
  {
    id: 'widal', name: 'Widal+Dengue', label: 'Widal + Dengue + MP Panel', sampleType: 'Blood', rate: 750, color: '#7c3aed',
    parameters: [
      { name: 'MP (Kit Method)', unit: '', refRange: 'Negative' },
      { name: 'S.T.Of "O" is', unit: '', refRange: 'Negative / < 1:80 Dilution' },
      { name: 'S.T.Of "H" is', unit: '', refRange: 'Negative / < 1:80 Dilution' },
      { name: 'S.P.T.Of "AH" & "BH" is', unit: '', refRange: 'Negative' },
      { name: 'NS1 (Dengue Antigen)', unit: '', refRange: 'Non-Reactive' },
      { name: 'IgG (Dengue)', unit: '', refRange: 'Non-Reactive' },
      { name: 'IgM (Dengue)', unit: '', refRange: 'Non-Reactive' },
      { name: 'HBs Ag (Hepatitis B)', unit: '', refRange: 'Non-Reactive' },
      { name: 'HIV 1&2', unit: '', refRange: 'Non-Reactive' },
      { name: 'HCV (Hepatitis C)', unit: '', refRange: 'Non-Reactive' },
    ],
  },
  {
    id: 'abg', name: 'ABG', label: 'Arterial Blood Gases (ABG)', sampleType: 'Arterial Blood', rate: 2000, color: '#059669',
    parameters: [
      { name: 'pH', unit: '', refLow: 7.35, refHigh: 7.45, refRange: '7.350–7.450' },
      { name: 'PCO2', unit: 'mmHg', refLow: 35, refHigh: 48, refRange: '35.0–48.0' },
      { name: 'pO2', unit: 'mmHg', refRange: '80–100' },
      { name: 'cHCO3-', unit: 'mmol/L', refLow: 21, refHigh: 28, refRange: '21.0–28.0' },
      { name: 'BE(ect)', unit: 'mmol/L', refLow: -2, refHigh: 3, refRange: '-2.0–3.0' },
      { name: 'cSO2', unit: '%', refLow: 94, refHigh: 98, refRange: '94–98.0' },
      { name: 'Na+', unit: 'mmol/L', refLow: 38, refHigh: 146, refRange: '38–146' },
      { name: 'K+', unit: 'mmol/L', refLow: 3.5, refHigh: 4.5, refRange: '3.5–4.5' },
      { name: 'Ca++', unit: 'mmol/L', refLow: 1.15, refHigh: 1.33, refRange: '1.15–1.33' },
      { name: 'Cl-', unit: 'mmol/L', refLow: 98, refHigh: 107, refRange: '98–107' },
      { name: 'cTCO2', unit: 'mmol/L', refLow: 22, refHigh: 29, refRange: '22.0–29.0' },
      { name: 'AGap', unit: 'mmol/L', refLow: 7, refHigh: 16, refRange: '7–16' },
      { name: 'Hct', unit: 'mmol/L', refLow: 38, refHigh: 51, refRange: '38–51' },
      { name: 'cHgb', unit: 'g/dl', refLow: 12, refHigh: 17, refRange: '12–17' },
      { name: 'BE(b)', unit: 'mmol/L', refLow: -2, refHigh: 3, refRange: '-2.0–3.0' },
      { name: 'Glucose (Glu)', unit: 'mg/dL', refLow: 74, refHigh: 100, refRange: '74–100' },
      { name: 'Lactate (Lac)', unit: 'mmol/L', refLow: 0.56, refHigh: 1.39, refRange: '0.56–1.39' },
      { name: 'Creatinine (Crea)', unit: 'mg/dL', refLow: 0.51, refHigh: 1.19, refRange: '0.51–1.19' },
    ],
  },
  {
    id: 'lft', name: 'LFT', label: 'Liver Function Test (LFT)', sampleType: 'Blood', rate: 600, color: '#ea580c',
    parameters: [
      { name: 'Total Bilirubin', unit: 'mg/dl', refLow: 0.2, refHigh: 1.2, refRange: '0.2–1.2 mg/dl' },
      { name: 'Direct Bilirubin', unit: 'mg/dl', refLow: 0, refHigh: 0.4, refRange: '0.0–0.4 mg/dl' },
      { name: 'SGOT (AST)', unit: 'U/L', refLow: 10, refHigh: 40, refRange: '10–40 U/L' },
      { name: 'SGPT (ALT)', unit: 'U/L', refLow: 7, refHigh: 56, refRange: '7–56 U/L' },
      { name: 'Alkaline Phosphatase', unit: 'U/L', refLow: 44, refHigh: 147, refRange: '44–147 U/L' },
      { name: 'Total Protein', unit: 'g/dl', refLow: 6, refHigh: 8, refRange: '6.0–8.0 g/dl' },
      { name: 'Albumin', unit: 'g/dl', refLow: 3.5, refHigh: 5, refRange: '3.5–5.0 g/dl' },
      { name: 'Globulin', unit: 'g/dl', refLow: 2, refHigh: 3.5, refRange: '2.0–3.5 g/dl' },
    ],
  },
  {
    id: 'rft', name: 'RFT/Electrolytes', label: 'RFT + Electrolytes', sampleType: 'Blood', rate: 750, color: '#0891b2',
    parameters: [
      { name: 'Blood Urea', unit: 'mg/dl', refLow: 15, refHigh: 45, refRange: '15–45 mg/dl' },
      { name: 'Serum Creatinine', unit: 'mg/dl', refLow: 0.6, refHigh: 1.4, refRange: '0.6–1.4 mg/dl' },
      { name: 'Uric Acid', unit: 'mg/dl', refLow: 2.6, refHigh: 7.2, refRange: '2.6–7.2 mg/dl' },
      { name: 'Sodium (Na+)', unit: 'mEq/L', refLow: 135, refHigh: 145, refRange: '135–145 mEq/L' },
      { name: 'Potassium (K+)', unit: 'mEq/L', refLow: 3.5, refHigh: 5, refRange: '3.5–5.0 mEq/L' },
      { name: 'Chloride (Cl-)', unit: 'mEq/L', refLow: 98, refHigh: 107, refRange: '98–107 mEq/L' },
    ],
  },
  {
    id: 'thyroid', name: 'Thyroid', label: 'Thyroid Function (TFT)', sampleType: 'Blood', rate: 900, color: '#9333ea',
    parameters: [
      { name: 'TSH', unit: 'µIU/mL', refLow: 0.5, refHigh: 5, refRange: '0.5–5.0 µIU/mL' },
      { name: 'T3 (Total)', unit: 'ng/dL', refLow: 80, refHigh: 200, refRange: '80–200 ng/dL' },
      { name: 'T4 (Total)', unit: 'µg/dL', refLow: 5, refHigh: 12, refRange: '5.0–12.0 µg/dL' },
      { name: 'Free T3 (fT3)', unit: 'pg/mL', refLow: 2.3, refHigh: 4.2, refRange: '2.3–4.2 pg/mL' },
      { name: 'Free T4 (fT4)', unit: 'ng/dL', refLow: 0.89, refHigh: 1.76, refRange: '0.89–1.76 ng/dL' },
    ],
  },
  {
    id: 'urine', name: 'Urine R/M', label: 'CLINICAL PATHOLOGY — Urine Examination R/M', sampleType: 'Urine (Mid-stream)', rate: 200, color: '#ca8a04',
    parameters: [
      { name: 'Volume', unit: 'ml', refRange: '' },
      { name: 'Colour', unit: '', refRange: '-PALE YELLOW' },
      { name: 'Transparency', unit: '', refRange: '-CLEAR' },
      { name: 'pH (Urine)', unit: '', refLow: 4.5, refHigh: 8.5, refRange: '4.5–8.5' },
      { name: 'Specific Gravity', unit: '', refRange: '1.005–1.030 (Normal)' },
      { name: 'Proteins', unit: '', refRange: '-NEGATIVE' },
      { name: 'Glucose', unit: '', refRange: '-NEGATIVE' },
      { name: 'Ketone Bodies', unit: '', refRange: '-NEGATIVE' },
      { name: 'Bilirubin', unit: '', refRange: '-NEGATIVE' },
      { name: 'Blood', unit: '', refRange: '-NEGATIVE' },
      { name: 'Pus Cells', unit: '/HPF', refLow: 0, refHigh: 5, refRange: '0–5' },
      { name: 'Red Blood Cells', unit: '/HPF', refRange: '-NIL' },
      { name: 'Epithelial Cells', unit: '/HPF', refLow: 0, refHigh: 5, refRange: '0–5' },
      { name: 'Casts', unit: '', refRange: '-NIL' },
      { name: 'Crystals', unit: '', refRange: '-NIL' },
      { name: 'Bacteria', unit: '', refRange: '-ABSENT' },
      { name: 'Amorphous Deposits', unit: '', refRange: 'Absent' },
      { name: 'Yeast Cells', unit: '', refRange: '-ABSENT' },
      { name: 'Spermatozoa', unit: '', refRange: 'Absent' },
    ],
  },
  {
    id: 'crp', name: 'CRP', label: 'IMMUNOLOGY — C-Reactive Protein Quantitative (CRP)', sampleType: 'Blood (Serum)', rate: 400, color: '#dc2626',
    parameters: [
      { name: 'C-Reactive Protein Level', unit: 'mg/L', refLow: 0, refHigh: 6, refRange: '0–6 mg/L (Normal)' },
      { name: 'Method: Turbidimetric Immunoassay', unit: '', refRange: '' },
      { name: 'ESR', unit: 'mm/hr', refLow: 0, refHigh: 20, refRange: 'M: <15 / F: <20 mm/hr' },
    ],
  },
  {
    id: 'ecg', name: 'ECG', label: 'ECG — 12 Lead Findings & Measurements', sampleType: 'ECG (12-Lead)', rate: 300, color: '#ef4444',
    parameters: [
      { name: 'Heart Rate', unit: 'bpm', refLow: 60, refHigh: 100, refRange: '60–100 bpm' },
      { name: 'PR Interval', unit: 'ms', refLow: 120, refHigh: 200, refRange: '120–200 ms' },
      { name: 'QRS Duration', unit: 'ms', refLow: 60, refHigh: 100, refRange: '60–100 ms' },
      { name: 'QT Interval', unit: 'ms', refLow: 350, refHigh: 440, refRange: '350–440 ms' },
      { name: 'QTc (Corrected)', unit: 'ms', refHigh: 440, refRange: '≤440ms (M) / ≤460ms (F)' },
      { name: 'Rhythm', unit: '', refRange: 'Normal Sinus Rhythm' },
      { name: 'Axis', unit: 'degrees', refRange: '-30° to +90° (Normal)' },
      { name: 'P Wave', unit: '', refRange: 'Normal' },
      { name: 'ST Segment', unit: '', refRange: 'Isoelectric (Normal)' },
      { name: 'T Wave', unit: '', refRange: 'Upright (Normal)' },
      { name: 'Overall Impression', unit: '', refRange: 'Within Normal Limits' },
    ],
  },
  {
    id: 'hba1c', name: 'HbA1c', label: 'HbA1c — Glycated Haemoglobin (Diabetes)', sampleType: 'Blood (EDTA)', rate: 600, color: '#7c3aed',
    parameters: [
      { name: 'HbA1c', unit: '%', refLow: 4, refHigh: 5.7, refRange: 'Normal: <5.7% | Pre-DM: 5.7–6.4% | DM: ≥6.5%' },
      { name: 'Fasting Blood Sugar (FBS)', unit: 'mg/dL', refLow: 70, refHigh: 100, refRange: '70–100 mg/dL' },
      { name: 'Post-Prandial Blood Sugar (PPBS)', unit: 'mg/dL', refHigh: 140, refRange: '< 140 mg/dL (2hr after meal)' },
      { name: 'Random Blood Sugar (RBS)', unit: 'mg/dL', refLow: 70, refHigh: 200, refRange: '70–200 mg/dL' },
    ],
  },
  {
    id: 'cardiac', name: 'Cardiac', label: 'Cardiac Markers — Heart Attack Panel', sampleType: 'Blood (Serum)', rate: 2500, color: '#991b1b',
    parameters: [
      { name: 'Troponin I (cTnI)', unit: 'ng/mL', refHigh: 0.04, refRange: '< 0.04 ng/mL (Normal)' },
      { name: 'Troponin T (cTnT)', unit: 'ng/mL', refHigh: 0.01, refRange: '< 0.01 ng/mL (Normal)' },
      { name: 'CK-MB (Creatine Kinase-MB)', unit: 'U/L', refLow: 0, refHigh: 25, refRange: '0–25 U/L' },
      { name: 'Total CK (Creatine Kinase)', unit: 'U/L', refLow: 30, refHigh: 200, refRange: 'M: 30–200 / F: 20–180 U/L' },
      { name: 'LDH (Lactate Dehydrogenase)', unit: 'U/L', refLow: 100, refHigh: 240, refRange: '100–240 U/L' },
      { name: 'BNP / NT-proBNP', unit: 'pg/mL', refHigh: 100, refRange: '< 100 pg/mL' },
      { name: 'D-Dimer', unit: 'ng/mL FEU', refHigh: 500, refRange: '< 500 ng/mL FEU' },
    ],
  },
  {
    id: 'coag', name: 'Coagulation', label: 'Coagulation Profile — PT / INR / APTT', sampleType: 'Blood (Citrate)', rate: 800, color: '#0369a1',
    parameters: [
      { name: 'Prothrombin Time (PT)', unit: 'seconds', refLow: 11, refHigh: 14, refRange: '11–14 seconds' },
      { name: 'INR', unit: '', refLow: 0.8, refHigh: 1.2, refRange: '0.8–1.2 (Normal)' },
      { name: 'APTT / aPTT', unit: 'seconds', refLow: 25, refHigh: 40, refRange: '25–40 seconds' },
      { name: 'Fibrinogen', unit: 'mg/dL', refLow: 200, refHigh: 400, refRange: '200–400 mg/dL' },
      { name: 'Bleeding Time (BT)', unit: 'minutes', refLow: 1, refHigh: 6, refRange: '1–6 minutes' },
      { name: 'Clotting Time (CT)', unit: 'minutes', refLow: 5, refHigh: 11, refRange: '5–11 minutes' },
    ],
  },
  {
    id: 'serolog', name: 'Serology', label: 'SEROLOGY — HIV / HBsAg / HCV / VDRL', sampleType: 'Blood (Serum)', rate: 1200, color: '#9333ea',
    parameters: [
      { name: 'HIV 1 & 2 (Screening)', unit: '', refRange: 'Non-Reactive' },
      { name: 'HBs Antigen (Hepatitis B)', unit: '', refRange: 'Non-Reactive' },
      { name: 'Anti-HCV (Hepatitis C)', unit: '', refRange: 'Non-Reactive' },
      { name: 'VDRL (Syphilis)', unit: '', refRange: 'Non-Reactive' },
      { name: 'Anti-HAV IgM (Hepatitis A)', unit: '', refRange: 'Non-Reactive' },
    ],
  },
  {
    id: 'covid', name: 'COVID-19', label: 'COVID-19 Antigen / Antibody Test', sampleType: 'Nasopharyngeal Swab / Blood', rate: 500, color: '#b91c1c',
    parameters: [
      { name: 'COVID-19 Antigen (Rapid)', unit: '', refRange: 'Negative' },
      { name: 'IgG Antibody (COVID)', unit: '', refRange: 'Non-Reactive' },
      { name: 'IgM Antibody (COVID)', unit: '', refRange: 'Non-Reactive' },
    ],
  },
];

function NewOrder({ catalog, user, onDone, onError }) {
  const [patientName, setPatientName] = useState('');
  const [age, setAge] = useState('');
  const [gender, setGender] = useState('');
  const [referredBy, setReferredBy] = useState('');
  const [uhid, setUhid] = useState('');
  const [ipNo, setIpNo] = useState('');
  const [source, setSource] = useState('OPD');
  const [picked, setPicked] = useState([]);
  const [sel, setSel] = useState('');
  const [busy, setBusy] = useState(false);

  const addPanel = useCallback((panel) => {
    const existing = picked.find((p) => p.testId === panel.id);
    if (existing) return;
    setPicked((ps) => [...ps, {
      testId: panel.id,
      name: panel.label,
      rate: panel.rate,
      sampleType: panel.sampleType,
      parameters: panel.parameters.map((p) => ({ ...p, value: '' })),
    }]);
  }, [picked]);

  const addTest = useCallback((testId) => {
    const t = catalog.find((c) => c.testId === testId);
    if (!t || picked.some((p) => p.testId === testId)) { setSel(''); return; }
    setPicked((ps) => [...ps, catalogToOrderTest(t)]);
    setSel('');
  }, [catalog, picked]);

  const removeTest = (testId) => setPicked((ps) => ps.filter((p) => p.testId !== testId));
  const total = picked.reduce((s, t) => s + Number(t.rate || 0), 0);

  const save = async () => {
    if (!patientName.trim()) { onError('Patient name is required.'); return; }
    if (!picked.length) { onError('Add at least one test.'); return; }
    setBusy(true);
    try {
      await ipApi.createLabOrder({
        patientName, age, gender, referredBy,
        uhid, admissionId: ipNo, source,
        tests: picked, status: 'ordered',
        hospitalId: user?.hospitalId,
      });
      onDone('Lab order created.');
    } catch (e) {
      onError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card">
      <h3>New Lab Order / Investigation</h3>
      <div className="form-grid mb">
        <div className="field"><label>Patient Name *</label><input className="input" value={patientName} onChange={(e) => setPatientName(e.target.value)} /></div>
        <div className="field"><label>Age</label><input className="input" placeholder="e.g. 54" value={age} onChange={(e) => setAge(e.target.value)} /></div>
        <div className="field"><label>Gender</label>
          <select className="input" value={gender} onChange={(e) => setGender(e.target.value)}>
            <option value="">—</option><option>Male</option><option>Female</option><option>Other</option>
          </select>
        </div>
        <div className="field"><label>UHID</label><input className="input" placeholder="U-XXXX/2025" value={uhid} onChange={(e) => setUhid(e.target.value)} /></div>
        <div className="field"><label>IPD No. (if admitted)</label><input className="input" placeholder="IP-534/2025" value={ipNo} onChange={(e) => setIpNo(e.target.value)} /></div>
        <div className="field"><label>Source</label>
          <select className="input" value={source} onChange={(e) => setSource(e.target.value)}>
            <option>OPD</option><option>IPD</option><option>Emergency</option><option>Walk-in</option>
          </select>
        </div>
        <div className="field"><label>Referred by (Doctor)</label><input className="input" placeholder="Dr. Name" value={referredBy} onChange={(e) => setReferredBy(e.target.value)} /></div>
      </div>

      {/* ── Quick Test Panels ── */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontWeight: 700, fontSize: 12, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
          ⚡ Quick Test Panels (click to add)
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {TEST_PANELS.map((panel) => {
            const isAdded = picked.some((p) => p.testId === panel.id);
            return (
              <button
                key={panel.id}
                type="button"
                onClick={() => isAdded ? removeTest(panel.id) : addPanel(panel)}
                style={{
                  padding: '6px 14px', borderRadius: 20, border: `2px solid ${panel.color}`,
                  background: isAdded ? panel.color : '#fff', color: isAdded ? '#fff' : panel.color,
                  fontWeight: 700, fontSize: 12, cursor: 'pointer', transition: 'all 0.15s',
                }}
              >
                {isAdded ? '✓ ' : '+ '}{panel.name} — ₹{panel.rate}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Or add from catalog ── */}
      <div className="field mb" style={{ maxWidth: 480 }}>
        <label>Or add from test catalog</label>
        <select className="input" value={sel} onChange={(e) => addTest(e.target.value)}>
          <option value="">— Select test from catalog —</option>
          {catalog.map((c) => <option key={c.testId} value={c.testId}>{c.name} · {inr(c.rate)}{c.sampleType ? ` · ${c.sampleType}` : ''}</option>)}
        </select>
        {!catalog.length && <p className="hint">No tests yet — add them in Test catalog first.</p>}
      </div>

      {/* ── Selected tests ── */}
      {picked.length > 0 && (
        <div className="table-wrap mb">
          <table className="data-table">
            <thead><tr><th>Test / Panel</th><th>Sample</th><th>Parameters ({picked.reduce((s, t) => s + t.parameters.length, 0)} total)</th><th>Rate</th><th></th></tr></thead>
            <tbody>
              {picked.map((t) => (
                <tr key={t.testId}>
                  <td><strong>{t.name}</strong></td>
                  <td>{t.sampleType}</td>
                  <td className="hint" style={{ fontSize: 11 }}>{t.parameters.slice(0, 6).map((p) => p.name).join(', ')}{t.parameters.length > 6 ? ` +${t.parameters.length - 6} more` : ''}</td>
                  <td>{inr(t.rate)}</td>
                  <td><button type="button" className="btn btn-sm btn-secondary" onClick={() => removeTest(t.testId)}><MdDelete /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="pharmacy-sale-foot">
        <div className="sale-totals">
          <div className="sale-grand" style={{ borderLeft: 'none', paddingLeft: 0 }}>
            <span>Total ({picked.length} test{picked.length !== 1 ? 's' : ''})</span>
            <strong>{inr(total)}</strong>
          </div>
        </div>
        <button type="button" className="btn btn-primary" disabled={busy || !picked.length} onClick={save}>
          {busy ? 'Saving…' : '✅ Create Lab Order'}
        </button>
      </div>
    </div>
  );
}

/* ── Test catalog ── */
function CatalogTab({ catalog, loading, onChanged, onError }) {
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_TEST);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const filtered = catalog.filter((c) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.name || '').toLowerCase().includes(q) || (c.category || '').toLowerCase().includes(q);
  });

  const startAdd = () => { setForm(EMPTY_TEST); setEditId(null); setShowForm(true); };
  const startEdit = (t) => {
    setForm({
      name: t.name || '', category: t.category || 'General', sampleType: t.sampleType || 'Blood',
      rate: t.rate ?? '', unit: t.unit || '', refRange: t.refRange || '', refLow: t.refLow ?? '', refHigh: t.refHigh ?? '',
      method: t.method || '', parameters: Array.isArray(t.parameters) ? t.parameters : [],
    });
    setEditId(t.testId);
    setShowForm(true);
  };

  const addParam = () => setForm((f) => ({ ...f, parameters: [...f.parameters, { name: '', unit: '', refRange: '', refLow: '', refHigh: '' }] }));
  const setParam = (i, patch) => setForm((f) => ({ ...f, parameters: f.parameters.map((p, j) => (j === i ? { ...p, ...patch } : p)) }));
  const removeParam = (i) => setForm((f) => ({ ...f, parameters: f.parameters.filter((_, j) => j !== i) }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { onError('Test name is required.'); return; }
    setBusy(true);
    try {
      if (editId) await ipApi.updateLabTest(editId, form);
      else await ipApi.createLabTest(form);
      setShowForm(false); setForm(EMPTY_TEST); setEditId(null);
      onChanged(editId ? 'Test updated.' : 'Test added to catalog.');
    } catch (err) {
      onError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    { key: 'name', label: 'Test', render: (r) => <div><strong>{r.name}</strong>{Array.isArray(r.parameters) && r.parameters.length > 0 && <div className="hint">{r.parameters.length} parameters</div>}</div> },
    { key: 'category', label: 'Category' },
    { key: 'sampleType', label: 'Sample' },
    { key: 'refRange', label: 'Reference', render: (r) => r.refRange || (r.refLow !== '' && r.refLow != null ? `${r.refLow}–${r.refHigh}` : (r.parameters?.length ? '—' : '—')) },
    { key: 'rate', label: 'Rate', render: (r) => inr(r.rate) },
    { key: 'actions', label: 'Actions', render: (r) => <button type="button" className="btn btn-sm btn-secondary" onClick={() => startEdit(r)}>Edit</button> },
  ];

  return (
    <>
      <div className="card mb inventory-toolbar">
        <input className="input" placeholder="Search test or category…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <button type="button" className="btn btn-primary" onClick={startAdd}><MdAdd /> Add test</button>
      </div>

      {showForm && (
        <form className="card mb" onSubmit={save}>
          <h3 style={{ marginTop: 0 }}>{editId ? 'Edit test' : 'Add test'}</h3>
          <div className="form-grid">
            <div className="field"><label>Test name *</label><input className="input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Complete Blood Count" /></div>
            <div className="field"><label>Category</label><input className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Haematology" /></div>
            <div className="field"><label>Sample type</label>
              <select className="input" value={form.sampleType} onChange={(e) => setForm({ ...form, sampleType: e.target.value })}>
                {['Blood', 'Serum', 'Plasma', 'Urine', 'Stool', 'Swab', 'Other'].map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="field"><label>Rate ₹</label><input className="input" type="number" min={0} value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} /></div>
            <div className="field"><label>Method</label><input className="input" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} placeholder="e.g. Automated / ELISA" /></div>
          </div>

          <div className="lab-params-head">
            <span>Parameters {form.parameters.length === 0 && <span className="hint">(leave empty for a single-value test — set unit/range below)</span>}</span>
            <button type="button" className="btn btn-sm btn-secondary" onClick={addParam}><MdAdd /> Add parameter</button>
          </div>

          {form.parameters.length === 0 ? (
            <div className="form-grid">
              <div className="field"><label>Unit</label><input className="input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="mg/dL" /></div>
              <div className="field"><label>Reference (text)</label><input className="input" value={form.refRange} onChange={(e) => setForm({ ...form, refRange: e.target.value })} placeholder="e.g. Negative / 70–110" /></div>
              <div className="field"><label>Ref low</label><input className="input" type="number" value={form.refLow} onChange={(e) => setForm({ ...form, refLow: e.target.value })} /></div>
              <div className="field"><label>Ref high</label><input className="input" type="number" value={form.refHigh} onChange={(e) => setForm({ ...form, refHigh: e.target.value })} /></div>
            </div>
          ) : (
            <div className="table-wrap mb">
              <table className="data-table">
                <thead><tr><th>Parameter</th><th>Unit</th><th>Ref text</th><th>Low</th><th>High</th><th></th></tr></thead>
                <tbody>
                  {form.parameters.map((p, i) => (
                    <tr key={i}>
                      <td><input className="input input-sm" value={p.name} onChange={(e) => setParam(i, { name: e.target.value })} placeholder="Haemoglobin" /></td>
                      <td><input className="input input-sm" style={{ width: 80 }} value={p.unit} onChange={(e) => setParam(i, { unit: e.target.value })} placeholder="g/dL" /></td>
                      <td><input className="input input-sm" style={{ width: 110 }} value={p.refRange} onChange={(e) => setParam(i, { refRange: e.target.value })} /></td>
                      <td><input className="input input-sm" style={{ width: 70 }} type="number" value={p.refLow} onChange={(e) => setParam(i, { refLow: e.target.value })} /></td>
                      <td><input className="input input-sm" style={{ width: 70 }} type="number" value={p.refHigh} onChange={(e) => setParam(i, { refHigh: e.target.value })} /></td>
                      <td><button type="button" className="btn btn-sm btn-secondary" onClick={() => removeParam(i)}><MdDelete /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => { setShowForm(false); setEditId(null); }}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : editId ? 'Save changes' : 'Add test'}</button>
          </div>
        </form>
      )}

      <DataTable columns={columns} rows={filtered} keyField="testId" loading={loading} emptyMessage={search ? 'No tests match your search.' : 'No tests yet — click “Add test” to build your catalog (CBC, LFT, KFT, etc.).'} />
    </>
  );
}

/* Critical Values Alert Tab — derived from live lab results */
function CriticalValuesTab({ orders = [] }) {
  const CRIT = [
    { param: 'Hemoglobin', low: 7, unit: 'g/dL', high: null, action: 'Transfusion consideration, inform doctor immediately' },
    { param: 'Potassium', low: 2.5, unit: 'mEq/L', high: 6.5, action: 'ECG monitoring, immediate electrolyte correction' },
    { param: 'Sodium', low: 120, unit: 'mEq/L', high: 160, action: 'Immediate physician notification, IV correction protocol' },
    { param: 'Glucose', low: 40, unit: 'mg/dL', high: 500, action: 'Hypoglycemia: IV dextrose. Hyperglycemia: Insulin protocol' },
    { param: 'Platelet', low: 20000, unit: '/uL', high: null, action: 'Platelet transfusion, bleeding precautions' },
    { param: 'INR', low: null, unit: '', high: 4.0, action: 'Reverse anticoagulation, FFP/Vitamin K' },
    { param: 'Creatinine', low: null, unit: 'mg/dL', high: 10, action: 'Nephrology consult, dialysis consideration' },
    { param: 'Troponin', low: null, unit: 'ng/mL', high: 0.4, action: 'STAT ECG, cardiology consult, ACS protocol' },
    { param: 'pH', low: 7.2, unit: '', high: 7.6, action: 'ICU consult, ventilator support' },
    { param: 'Lactate', low: null, unit: 'mmol/L', high: 4, action: 'Sepsis protocol, ICU alert, fluid resuscitation' },
  ];

  const alertsFromOrders = useMemo(() => {
    const matchCrit = (name) => {
      const n = (name || '').toLowerCase();
      return CRIT.find((c) => n.includes(c.param.toLowerCase()) || c.param.toLowerCase().includes(n));
    };
    const out = [];
    orders
      .filter((o) => ['resulted', 'verified', 'Approved', 'completed'].includes(o.status))
      .forEach((order) => {
        (order.tests || []).forEach((test) => {
          (test.parameters || []).forEach((p) => {
            const crit = matchCrit(p.name);
            if (!crit) return;
            const v = Number(p.value);
            if (p.value === '' || p.value == null || Number.isNaN(v)) return;
            let flag = null;
            if (crit.low != null && v < crit.low) flag = 'CRITICAL LOW';
            if (crit.high != null && v > crit.high) flag = 'CRITICAL HIGH';
            if (!flag) return;
            out.push({
              id: `${order.orderId}-${p.name}`,
              patient: order.patientName || 'Patient',
              ipId: order.patientId || order.ipId || '—',
              param: p.name,
              value: String(p.value),
              unit: p.unit || crit.unit,
              flag,
              at: order.verifiedAt || order.resultedAt || order.createdAt,
              action: crit.action,
            });
          });
        });
      });
    return out.sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));
  }, [orders]);

  const [notified, setNotified] = React.useState({});
  const [notifyName, setNotifyName] = React.useState({});
  const alerts = alertsFromOrders.map((a) => ({
    ...a,
    notified: !!notified[a.id],
    notifiedTo: notified[a.id] || '',
  }));
  const unnotified = alerts.filter((a) => !a.notified);

  function markNotified(id) {
    setNotified((prev) => ({ ...prev, [id]: notifyName[id] || 'Duty Doctor' }));
  }

  return (
    <div>
      {unnotified.length > 0 && (
        <div style={{ padding: '12px 16px', background: '#fef2f2', border: '2px solid #dc2626', borderRadius: 10, marginBottom: 16 }}>
          <div style={{ fontWeight: 800, color: '#dc2626', fontSize: 14 }}>ALERT: {unnotified.length} UNNOTIFIED CRITICAL VALUE{unnotified.length > 1 ? 'S' : ''}! Report to treating doctor immediately.</div>
        </div>
      )}
      <div className="card mb">
        <h3 style={{ marginTop: 0, color: '#dc2626' }}>Active Critical Value Alerts</h3>
        {alerts.length === 0 && <p className="hint table-empty">No critical values. All results within safe limits.</p>}
        {alerts.map(a => (
          <div key={a.id} style={{ padding: '12px 14px', borderBottom: '1px solid #f1f5f9', background: a.notified ? '#fff' : '#fef2f2' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
              <div>
                <div style={{ fontWeight: 800, fontSize: 14 }}>{a.patient} <span style={{ color: '#94a3b8', fontWeight: 400, fontSize: 12 }}>{a.ipId}</span></div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
                  <span style={{ fontFamily: 'Outfit,sans-serif', fontSize: 20, fontWeight: 900, color: '#dc2626' }}>{a.value} {a.unit}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 999, background: '#fef2f2', color: '#dc2626', border: '1px solid #fca5a5' }}>{a.flag}</span>
                  <span style={{ fontSize: 12, color: '#64748b' }}>{a.param}</span>
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: '#94a3b8' }}>{new Date(a.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
                {a.notified
                  ? <div style={{ fontSize: 11, color: '#10b981', fontWeight: 700, marginTop: 4 }}>Notified to {a.notifiedTo}</div>
                  : <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                      <input className="input" style={{ fontSize: 11, padding: '2px 6px', maxWidth: 120 }} placeholder="Doctor name" value={notifyName[a.id] || ''} onChange={e => setNotifyName(n => ({ ...n, [a.id]: e.target.value }))} />
                      <button type="button" style={{ fontSize: 11, padding: '2px 8px', background: '#dc2626', border: 'none', color: '#fff', borderRadius: 5, cursor: 'pointer', fontWeight: 700 }} onClick={() => markNotified(a.id)}>Notified</button>
                    </div>}
              </div>
            </div>
            {a.action && <div style={{ fontSize: 12, padding: '4px 10px', background: '#f0fdf4', borderRadius: 6, color: '#16a34a' }}>Action: {a.action}</div>}
          </div>
        ))}
      </div>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Critical Value Reference Ranges</h3>
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Parameter</th><th>Critical Low</th><th>Critical High</th><th>Required Action</th></tr></thead>
            <tbody>
              {CRIT.map(r => (
                <tr key={r.param}>
                  <td style={{ fontWeight: 600 }}>{r.param}</td>
                  <td style={{ color: '#2563eb', fontWeight: 700 }}>{r.low ? '< ' + r.low + ' ' + r.unit : '\u2014'}</td>
                  <td style={{ color: '#dc2626', fontWeight: 700 }}>{r.high ? '> ' + r.high + ' ' + r.unit : '\u2014'}</td>
                  <td style={{ fontSize: 12, color: '#64748b' }}>{r.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* Sample Collection Tab — wired to lab orders */
function SampleCollectionTab({ orders, onCollect }) {
  const [busy, setBusy] = React.useState('');
  const today = new Date().toISOString().slice(0, 10);
  const pending = (orders || []).filter((o) => ['ordered', 'pending'].includes(o.status) && !o.collectedAt);
  const collected = (orders || []).filter((o) => {
    if (!(o.status === 'collected' || o.collectedAt)) return false;
    const day = String(o.collectedAt || o.updatedAt || '').slice(0, 10);
    return !day || day === today;
  });

  return (
    <div>
      <div className="card" style={{ marginBottom: 14 }}>
        <h3 style={{ marginTop: 0 }}>Pending sample collection ({pending.length})</h3>
        {pending.length === 0 && <p className="hint">No lab orders awaiting sample collection.</p>}
        {pending.map((o) => (
          <div key={o.orderId} style={{ padding: '12px 0', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div style={{ fontWeight: 700 }}>{o.patientName || '—'}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>{o.tests || o.items || '—'}</div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>{o.orderId} · {o.doctorName || o.doctor || '—'}</div>
            </div>
            <button type="button" className="btn btn-primary btn-sm" disabled={busy === o.orderId} onClick={async () => {
              setBusy(o.orderId);
              try { await onCollect(o); } finally { setBusy(''); }
            }}>{busy === o.orderId ? 'Saving…' : 'Mark collected'}</button>
          </div>
        ))}
      </div>
      <div className="card">
        <h3 style={{ marginTop: 0 }}>Collected today ({collected.length})</h3>
        {collected.slice(0, 20).map((o) => (
          <div key={o.orderId} style={{ padding: '8px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13 }}>
            <strong>{o.patientName}</strong> — {(o.collectedAt || o.updatedAt || '').slice(0, 16).replace('T', ' ')}
          </div>
        ))}
      </div>
    </div>
  );
}
